import { ApolloCache, DefaultContext, MutationFunction } from "@apollo/client";
import duplicateIcon from "@assets/images/description.svg";
// import pencilIcon from "@assets/images/pencil-alt.svg";
import exclamationModal from "@assets/images/exclamationModal.svg";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from "@material-ui/core";
import { RemoveRedEyeOutlined } from "@material-ui/icons";
import { Skeleton } from "@material-ui/lab";
import { SubmitFilterDataType } from "@saleor/changeOrderDomestic/components/OrderFilterSection/constant";
import {
  changeOrderPath,
  DomesticOrderListSortField,
  notRefchangeOrderPath
} from "@saleor/changeOrderDomestic/urls";
import { ViewEditMode } from "@saleor/changeOrderDomestic/views/ViewEditOrder/types";
import MyTypography from "@saleor/components/MyTypography";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { BIG_ROW_NUMBER_SET } from "@saleor/components/TablePagination/constants";
import TablePaginationCustom from "@saleor/components/TablePaginationCustom";
import DialogConfirmCopyOrder from "@saleor/createOrChangeNotRefContract/components/DialogConfirmCopyOrder/DialogConfirmCopyOrder";
import DialogCreditBlock from "@saleor/createOrChangeNotRefContract/components/DialogCreditBlock";
import DialogCustomerBlock from "@saleor/createOrChangeNotRefContract/components/DialogCustomerBlock";
import { getCashSaleStatusText } from "@saleor/createOrChangeNotRefContract/utils";
import {
  DuplicateOrderCipMutation,
  DuplicateOrderMutation,
  Exact,
  SapChangeOrderQuery,
  useGetCreditLimitLazyQuery,
  useGetCustomerBlockLazyQuery
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { makeStyles } from "@saleor/macaw-ui";
import { renderCollection } from "@saleor/misc";
import { ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import classNames from "classnames";
// import moment from "moment-timezone";
import React, { useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";

enum EStatusType {
  "Received Order" = "#A3AED0",
  "Credit/Cash Issue" = "#FFC107",
  "Partial Committed Order" = "#3860BE",
  "Ready for delivery" = "#007BFF",
  "Partial Delivery" = "#009FB8",
  "Completed Delivery" = "#6CC04A",
  "Cancelled" = "#E41B17",
  "Completed Order" = "#1E7D34"
}

type ReturnTypeUseListSettings = ReturnType<typeof useListSettings>;

export interface IOrderListTable
  extends ListProps,
    SortPage<DomesticOrderListSortField> {
  data: SapChangeOrderQuery["sapChangeOrder"];
  settings: ReturnTypeUseListSettings["settings"];
  onUpdateListSettings: ReturnTypeUseListSettings["updateListSettings"];
  loading?: boolean;
  totalCount: number | null;
  latestPageItemNumber: number | null;
  onPreviousFirstPage: () => void;
  onNextLastPage: () => void;
  onDuplicateOrder: MutationFunction<
    DuplicateOrderMutation,
    Exact<{
      id: string;
    }>,
    DefaultContext,
    ApolloCache<any>
  >;
  onDuplicateCipOrder: MutationFunction<
    DuplicateOrderCipMutation,
    Exact<{
      soNo: string;
    }>,
    DefaultContext,
    ApolloCache<any>
  >;
  filter?: SubmitFilterDataType;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      overflowX: "auto",
      width: "100%",
      border: `1px solid ${theme.palette.grey[300]}`,
      borderRadius: "2px",
      "& .MuiTableCell-head": {
        "& *": {
          color: `${theme.palette.grey[900]} !important`,
          whiteSpace: "nowrap"
        }
      },
      "&::-webkit-scrollbar": {
        height: "14px",
        background: "#FFFFFF"
      },
      "&::-webkit-scrollbar-track": {
        borderRadius: 10
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#CCCCCC",
        borderRadius: 10,
        border: "4px solid #FFFFFF",
        height: "6px"
      }
    },
    tableContainer: {
      maxHeight: "60vh"
    },
    cursorPointer: {
      cursor: "pointer"
    },
    link: {
      cursor: "pointer"
    },
    colPiNumber: {
      paddingLeft: 20,
      background: "inherit",
      borderRight: `2px solid ${theme.palette.grey[300]}`,
      position: "sticky",
      left: 125,
      zIndex: 3,
      whiteSpace: "nowrap",
      "& .MuiTypography-root": {
        color: theme.palette.info.main
      }
    },
    col120: {
      minWidth: 120,
      color: theme.palette.grey[900]
    },
    col150: {
      minWidth: 150,
      color: theme.palette.grey[900]
    },
    col180: {
      minWidth: 180,
      color: theme.palette.grey[900]
    },
    iconModal: {
      position: "absolute",
      left: "45%",
      top: "20px"
    },
    inlineBlock: {
      display: "inline-block",
      color: theme.palette.grey[900]
    },
    pagination: {
      background: "rgba(212, 244, 248, 0.8)"
    },
    paginateFooter: {
      padding: "12px 20px",
      paddingLeft: "0"
    },
    rowClickable: {
      cursor: "pointer"
    },
    colFreeze: {
      position: "sticky",
      right: 0,
      borderLeft: `1px solid ${theme.palette.grey[300]}`,
      minWidth: 48,
      padding: 0,
      boxShadow: "-4px 0px 8px rgb(0 0 0 / 4%)",
      borderRadius: "0px 2px 2px 0px",
      background: "inherit",
      textAlign: "center"
    },
    colViewDuplicate: {
      position: "sticky",
      left: 0,
      display: "table-cell",
      justifyItems: "center",
      // borderRight: `1px solid ${theme.palette.grey[300]}`,
      minWidth: 125,
      paddingRight: "0px !important",
      paddingLeft: "12px !important",
      fontStyle: "italic",
      zIndex: 3,
      fontWeight: 400,
      borderRadius: "0px 2px 2px 0px",
      background: "inherit",
      textAlign: "center",
      "& svg": {
        color: "#999999"
      }
    },
    statusIcon: {
      width: 8,
      height: 8,
      marginRight: 8,
      borderRadius: "50%"
    },
    title: {
      marginTop: 40,
      marginBottom: 24
    },
    flex: { display: "flex", alignItems: "center" },
    firstChild: {
      paddingLeft: 32
    },
    zIndex1: { zIndex: 1 },
    cashSaleSatatusError: {
      color: theme.palette.error.light
    }
  }),
  { name: "OrderListTable" }
);

const OrderListTable: React.FC<IOrderListTable> = props => {
  const {
    data,
    disabled,
    settings,
    totalCount,
    pageInfo,
    latestPageItemNumber,
    onUpdateListSettings,
    sort,
    onSort,
    onPreviousPage,
    onNextPage,
    onNextLastPage,
    onPreviousFirstPage,
    filter,
    onDuplicateOrder,
    onDuplicateCipOrder
  } = props;
  const intl = useIntl();
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigator();
  const [_, dispatchAppState] = useAppState();

  const notify = useNotifier();
  const [openCustomerBlock, setOpenCustomerBlock] = useState<boolean>(false);
  const [openCreditBlock, setOpenCreditBlock] = useState<boolean>(false);

  const [customerBlockCompleted, setCustomerBlockCompleted] = useState(false);
  const [creditLimitCompleted, setCreditLimitCompleted] = useState(false);
  const [itemSelected, setItemSelected] = useState(null);
  const [openCopyOrder, setOpenCopyOrder] = useState<boolean>(false);

  const [getCustomerBlock] = useGetCustomerBlockLazyQuery({
    fetchPolicy: "no-cache",
    onCompleted(data) {
      if (data.getCustomerBlock.customerBlock) {
        setOpenCreditBlock(false);
        setOpenCustomerBlock(true);
        setCustomerBlockCompleted(false);
        setCopyOrderLoading(false);
      } else {
        setCustomerBlockCompleted(true);
      }
    },
    onError: () => {
      setCopyOrderLoading(false);
    }
  });

  const [getCreditLimitLazy] = useGetCreditLimitLazyQuery({
    fetchPolicy: "no-cache",
    onCompleted: data => {
      if (data?.getCreditLimit?.creditBlockStatus && !openCustomerBlock) {
        setOpenCustomerBlock(false);
        setOpenCreditBlock(true);
        setCreditLimitCompleted(false);
        setCopyOrderLoading(false);
      } else {
        setCreditLimitCompleted(true);
      }
    },
    onError: error => {
      setCopyOrderLoading(false);
      notify({
        status: "error",
        apiMessage: error.message
      });
    }
  });

  const renderStatus = (item: string) => (
    <div className={classes.flex}>
      <div
        className={classes.statusIcon}
        style={{ backgroundColor: EStatusType[item] }}
      ></div>
      <MyTypography variant="body3">{item}</MyTypography>
    </div>
  );

  const viewOrder = item => {
    dispatchAppState({
      type: "setViewEditMode",
      payload: {
        viewEditMode: ViewEditMode.VIEW
      }
    });
    dispatchAppState({
      type: "setFilterOrder",
      payload: {
        filterOrder: filter
      }
    });
    if (item?.isNotRef) {
      navigate(notRefchangeOrderPath(item?.soNo));
    } else {
      navigate(changeOrderPath(item?.soNo));
    }
  };

  const [copyOrderLoading, setCopyOrderLoading] = useState<boolean>(false);
  const copyOrder = () => {
    setCopyOrderLoading(true);
    getCustomerBlock({
      variables: {
        input: {
          soldToCode: itemSelected?.soldToCode,
          saleOrganizationCode: itemSelected?.salesOrgCode ?? null
        }
      }
    });

    getCreditLimitLazy({
      variables: {
        input: {
          salesOrgCode: itemSelected?.salesOrgCode,
          soldToCode: itemSelected?.soldToCode
        }
      }
    });
  };

  React.useEffect(() => {
    if (customerBlockCompleted && creditLimitCompleted) {
      if (itemSelected?.isNotRef) {
        onDuplicateCipOrder({
          variables: {
            soNo: itemSelected?.soNo
          }
        }).finally(() => {
          setCopyOrderLoading(false);
        });
      } else {
        onDuplicateOrder({
          variables: {
            id: itemSelected?.soNo
          }
        }).finally(() => {
          setCopyOrderLoading(false);
        });
      }
      setOpenCustomerBlock(false);
      setOpenCreditBlock(false);
      setCustomerBlockCompleted(false);
    }
  }, [customerBlockCompleted, creditLimitCompleted]);

  return (
    <>
      <MyTypography className={classes.title} variant="body1">
        {intl.formatMessage({ defaultMessage: "รายการที่เกี่ยวข้อง" })}{" "}
        {totalCount ? `(${totalCount})` : ""}
      </MyTypography>
      <div className={classes.root} ref={containerRef}>
        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader data-testid="orderList-table">
            <TableHead>
              <TableRow>
                <TableCellHeader className={classes.colViewDuplicate}>
                  <MyTypography variant="body3" style={{ fontSize: "13px" }}>
                    {intl.formatMessage({ defaultMessage: "View / Copy" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.colPiNumber}
                  direction={
                    sort.sort === DomesticOrderListSortField.piNo
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(DomesticOrderListSortField.piNo)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "S/O No." })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classNames(classes.col120, classes.firstChild)}
                  direction={
                    sort.sort === DomesticOrderListSortField.poNo
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(DomesticOrderListSortField.poNo)}
                >
                  <MyTypography variant="subtitle3">
                    <Box pl={1}>
                      {intl.formatMessage({ defaultMessage: "PO No." })}
                    </Box>
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  direction={
                    sort.sort === DomesticOrderListSortField.contractNo
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(DomesticOrderListSortField.contractNo)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "Contract No." })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col180}
                  direction={
                    sort.sort === DomesticOrderListSortField.soldtoParty
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(DomesticOrderListSortField.soldtoParty)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "Sold-to Party" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader className={classes.col180}>
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "Project Name" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader className={classes.col150}>
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "Sales Org." })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader className={classes.col120}>
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "Payment Terms" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader className={classes.col120}>
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "Credit status of document"
                    })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader className={classes.col120}>
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "Cash Sale Status"
                    })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  direction={
                    sort.sort === DomesticOrderListSortField.createDate
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(DomesticOrderListSortField.createDate)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "Create Date" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader className={classes.col120}>
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "Order Status" })}
                  </MyTypography>
                  <MyTypography variant="body3">
                    {intl.formatMessage({ defaultMessage: "(SAP)" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader className={classes.col120}>
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "Order Status" })}
                  </MyTypography>
                  <MyTypography variant="body3">
                    {intl.formatMessage({ defaultMessage: "(eOrdering)" })}
                  </MyTypography>
                </TableCellHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderCollection(data, (item, index) => (
                <TableRow className={classes.rowClickable} key={index}>
                  <TableCell
                    className={classNames(
                      classes.colViewDuplicate,
                      classes.zIndex1
                    )}
                    width={120}
                  >
                    {/* <SVG src={pencilIcon} onClick={() => editOrder(item.id)} /> */}
                    <Box justifyContent={"center"} className={classes.flex}>
                      <Tooltip title={"View order"} placement={"bottom"}>
                        <RemoveRedEyeOutlined
                          data-testid="viewOrderButton"
                          onClick={() => viewOrder(item)}
                          height={"15.9"}
                        />
                      </Tooltip>
                      <Box mr={3} />
                      <Tooltip title={"Copy order"} placement={"bottom"}>
                        <div className={classes.flex}>
                          <SVG
                            data-testid="copyOrderButton"
                            src={duplicateIcon}
                            onClick={() => {
                              setItemSelected(item);
                              setOpenCopyOrder(true);
                            }}
                          />
                        </div>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell
                    className={classNames(classes.colPiNumber, classes.zIndex1)}
                    width={120}
                  >
                    <MyTypography
                      variant="subtitle3"
                      onClick={() => viewOrder(item)}
                    >
                      {item ? (
                        <MyTypography variant="subtitle3">
                          {item.soNo}
                        </MyTypography>
                      ) : (
                        <Skeleton />
                      )}
                    </MyTypography>
                  </TableCell>
                  <TableCell
                    className={classNames(classes.col120, classes.firstChild)}
                  >
                    {item ? <Box pl={1}>{item.poNo}</Box> : <Skeleton />}
                  </TableCell>
                  <TableCell
                    className={classNames(classes.col120)}
                    align="left"
                  >
                    {item?.bu === "PP" ? (
                      <MyTypography variant="body3">
                        {item?.contractNo}
                      </MyTypography>
                    ) : (
                      <strong>-</strong>
                    )}
                  </TableCell>
                  <TableCell className={classes.col120}>
                    {item ? item.soldToParty : <Skeleton />}
                  </TableCell>
                  <TableCell className={classes.col180}>
                    {item?.bu === "PP" ? (
                      <MyTypography variant="body3">
                        {item?.projectName}
                      </MyTypography>
                    ) : (
                      <strong>-</strong>
                    )}
                  </TableCell>
                  <TableCell className={classes.col150}>
                    {item ? (
                      <MyTypography variant="body3">
                        {item.company}
                      </MyTypography>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.col150}>
                    {item ? (
                      <MyTypography variant="body3">
                        {item.paymentTerms}
                      </MyTypography>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.col120}>
                    {item ? item.creditStatusOfDocument : <Skeleton />}
                  </TableCell>
                  <TableCell className={classes.col120}>
                    {item ? (
                      <MyTypography
                        variant="body3"
                        className={classNames({
                          [classes.cashSaleSatatusError]:
                            item.cashSale && !item.cashSale?.status
                        })}
                      >
                        {getCashSaleStatusText(
                          item.cashSale?.statusName,
                          null
                        ) || <strong>-</strong>}
                      </MyTypography>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.col120}>
                    {item ? (
                      <MyTypography variant="body3">
                        {item.orderDate ? item.orderDate : null}
                      </MyTypography>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.col120}>
                    {item ? (
                      <MyTypography variant="body3">
                        {item.orderStatusSap}
                      </MyTypography>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.col180}>
                    {item ? (
                      <MyTypography variant="body3">
                        {renderStatus(item.orderStatusEOrdering)}
                      </MyTypography>
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  {/* <TableCell className={classes.col120}>
                    {item ? (
                      item.salesOrganization.businessUnit.name
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.col120}>
                    {item ? (
                      `${item.salesOrganization.code} - ${item.salesOrganization.name}`
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.col120}>
                    {item ? (
                      moment(item.createdAt).format("DD/MM/YYYY")
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell className={classes.col180}>
                    {item ? item.statusSap : <Skeleton />}
                  </TableCell>
                  // <TableCell className={classes.col180}>
                  //   {item ? renderStatus(item.status) : <Skeleton />}
                  // </TableCell> */}
                  {/* <TableCell className={classes.colFreeze}>
                    <SVG src={pencilIcon} onClick={() => editOrder(item.id)} />
                  </TableCell> */}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {data?.length ? (
          <StickyHorizontalScrollBar container={containerRef} />
        ) : null}
      </div>
      <Box className={classes.paginateFooter} data-testid="pagination-wrapper">
        <TablePaginationCustom
          settings={settings}
          hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
          onNextPage={onNextPage}
          onPreviousFirstPage={onPreviousFirstPage}
          onNextLastPage={onNextLastPage}
          onUpdateListSettings={onUpdateListSettings}
          hasPreviousPage={
            pageInfo && !disabled ? pageInfo.hasPreviousPage : false
          }
          onPreviousPage={onPreviousPage}
          latestPageItemNumber={latestPageItemNumber}
          totalCount={totalCount || 0}
          disabled={disabled}
          isBackFistPage
          isNextLastPage
          selectColor="#007E92"
          rowNumberSet={BIG_ROW_NUMBER_SET}
        />
      </Box>
      <DialogConfirmCopyOrder
        open={openCopyOrder}
        loading={copyOrderLoading}
        onConfirm={copyOrder}
        onClose={() => setOpenCopyOrder(false)}
      />
      <DialogCustomerBlock
        onConfirm={() => {
          setOpenCustomerBlock(false);
        }}
        open={openCustomerBlock}
        okText="ปิดหน้าต่าง"
        title="Customer Blocked"
        content={
          <>
            <img
              className={classes.iconModal}
              src={exclamationModal}
              alt="customer blocked"
            />
            <MyTypography variant="body2" className={classes.inlineBlock}>
              ไม่สามารถดำเนินการได้ เนื่องจากลูกค้ารายนี้ถูก{" "}
              <MyTypography variant="subtitle2" className={classes.inlineBlock}>
                Block{" "}
              </MyTypography>
              กรุณาติดต่อเจ้าหน้าที่สินเชื่อ
            </MyTypography>
          </>
        }
      />
      <DialogCreditBlock
        onConfirm={() => {
          setOpenCreditBlock(false);
        }}
        open={openCreditBlock}
        okText="ปิดหน้าต่าง"
        title="Credit Blocked"
        content={
          <>
            <img
              className={classes.iconModal}
              src={exclamationModal}
              alt="customer blocked"
            />
            <MyTypography variant="body2" className={classes.inlineBlock}>
              ไม่สามารถทำรายการได้ เนื่องจากลูกค้าติด{" "}
              <MyTypography variant="subtitle2" className={classes.inlineBlock}>
                Credit Blocked
              </MyTypography>{" "}
              กรุณาติดต่อเจ้าหน้าที่สินเชื่อ
            </MyTypography>
          </>
        }
      />
    </>
  );
};

export default OrderListTable;
